import React, { useMemo, useCallback } from 'react';
import { Tabs } from 'antd';
import type { TabsProps as AntTabsProps, TabsProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { createCommand } from '../../base/actionParser';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';
import type { AndTabPaneProps } from '../AndTabPane';

// Tab 配置项类型（兼容 original 项目格式）
export interface TabItem {
  tab: string;  // 显示名称
  key: string;  // 唯一标识
  disabled?: boolean;
}

export interface AndTabsProps extends Omit<AntTabsProps, 'onChange' | 'items'>, BaseComponentProps, DataSetConfig {
  /** Tab 配置列表（original 格式） */
  tabs?: TabItem[] | string;
  /** Antd 5.x items 格式 */
  items?: TabsProps['items'];
  /** 变更动作 */
  changeAction?: string;
  /** onChange 回调 */
  onChange?: (activeKey: string) => void;
  /** 开发模式 */
  develop?: boolean;
  /** Tab 内容渲染函数 */
  renderTabContent?: (key: string, index: number) => React.ReactNode;
}

// 解析 tabs 配置
const parseTabs = (tabs: TabItem[] | string | undefined): TabItem[] => {
  if (!tabs) return [];
  if (typeof tabs === 'string') {
    try {
      return JSON.parse(tabs);
    } catch {
      return [];
    }
  }
  return tabs;
};

/** 将 AndTabPane children 转为 items（对齐 antd useLegacyItems 的 children 分支） */
function paneChildrenToItems(
  children: React.ReactNode,
  develop?: boolean,
): TabsProps['items'] {
  return React.Children.toArray(children)
    .filter(React.isValidElement)
    .map((node) => {
      const { key, props } = node;
      const { tab, ...restProps } = (props ?? {}) as AndTabPaneProps;
      return {
        key: String(key),
        ...restProps,
        label: tab,
        // 导出页配合 tab.css 显隐：三面板须同时存在于 DOM
        forceRender: develop ? true : restProps.forceRender ?? true,
      };
    })
    .filter((item) => item.key);
}

export const AndTabs: React.FC<AndTabsProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  tabs,
  items: propItems,
  changeAction,
  onChange,
  activeKey: propActiveKey,
  defaultActiveKey,
  renderTabContent,
  children,
  ...restProps
}) => {
  const addCommand = useAndStore((state) => state.addCommand);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  const storeActiveKey = useAndStore((s) => {
    if (!dataSetKey || !dataSetField) return undefined;
    return s.getDataSet(dataSetKey, dataSetField);
  });

  // 解析 tabs 配置
  const parsedTabs = useMemo(() => parseTabs(tabs), [tabs]);

  const hasPaneChildren = React.Children.count(children) > 0;

  const paneItems = useMemo(
    () => (hasPaneChildren ? paneChildrenToItems(children, develop) : undefined),
    [children, develop, hasPaneChildren],
  );

  // 将 tabs 转换为 Antd 5.x items 格式
  const computedItems = useMemo(() => {
    // 优先使用 propItems（直接传入的 items）
    if (propItems && propItems.length > 0) {
      // 在开发模式下，为每个 item 添加 forceRender
      if (develop) {
        return propItems.map(item => ({ ...item, forceRender: true }));
      }
      return propItems;
    }

    // 将 tabs 格式转换为 items 格式
    if (parsedTabs.length > 0) {
      return parsedTabs.map((item, index) => ({
        key: item.key,
        label: item.tab,
        disabled: item.disabled,
        // 开发模式下强制渲染所有 TabPane，以便设计每个 Tab 的内容
        forceRender: develop ? true : undefined,
        children: renderTabContent ? renderTabContent(item.key, index) : null,
      }));
    }

    return [];
  }, [propItems, parsedTabs, renderTabContent, develop]);

  const resolvedItems = paneItems ?? computedItems;

  // 内部 activeKey 状态（用于设计模式下的 Tab 切换）
  const [internalActiveKey, setInternalActiveKey] = React.useState<string | undefined>(undefined);

  // 计算当前激活的 key
  const activeKey = useMemo(() => {
    // 在设计模式下，优先使用内部状态
    if (develop && internalActiveKey) {
      return internalActiveKey;
    }
    const key =
      storeActiveKey ?? propActiveKey ?? defaultActiveKey;
    // 如果没有指定 key，默认选中第一个
    if (!key && resolvedItems.length > 0) {
      return resolvedItems[0].key;
    }
    return key;
  }, [develop, internalActiveKey, storeActiveKey, propActiveKey, defaultActiveKey, resolvedItems]);

  // 处理 Tab 切换
  const handleChange = useCallback((key: string) => {
    // 在设计模式下，直接更新内部状态
    if (develop) {
      setInternalActiveKey(key);
      return;
    }
    if (onChange) {
      onChange(key);
      return;
    }
    if (dataSetKey && dataSetField) {
      setDataSet(dataSetKey, dataSetField, key);
    }
    if (changeAction && sessionId) {
      addCommand(sessionId, createCommand(changeAction, key));
    }
  }, [develop, onChange, dataSetKey, dataSetField, setDataSet, changeAction, sessionId, addCommand]);

  return (
    <Tabs
      id={id}
      {...restProps}
      activeKey={activeKey}
      items={resolvedItems}
      onChange={handleChange}
      destroyOnHidden={hasPaneChildren ? false : restProps.destroyOnHidden}
      // 在开发模式下禁用动画，防止非激活 TabPane 阻挡点击事件
      animated={develop ? false : restProps.animated}
    />
  );
};

export default AndTabs;
